/*
 * Diabec Human Call — website client controller.
 *
 * Mounts a floating "Talk to a specialist" button. Connects to the backend
 * web-calls signaling WebSocket as an anonymous (or signed-in) visitor so a
 * human agent on dashboard/web-calls.html can see who's online, ring them, and
 * be rung by them. Audio always flows over LiveKit (per Diabec's WebRTC stance).
 *
 * Two directions:
 *   - Visitor-initiated: click FAB → consent → mint LiveKit room+token from
 *     /voice/guest/token → join room → tell the agent dashboard "ring".
 *   - Agent-initiated: dashboard rings → incoming banner → Accept mints the
 *     room+token, joins, and tells the dashboard "accept".
 *
 * Config (optional globals, set before this script loads):
 *   window.DIABEC_API_BASE   — backend origin (default https://diabec-api.fly.dev)
 *   window.DIABEC_VISITOR    — { name, user_id } for signed-in users
 *
 * Depends on LiveKit's browser SDK. Uses window.LivekitClient (UMD) if present
 * (the AI voice widget already loads it); otherwise lazy-loads the UMD bundle.
 */
(function () {
  'use strict';
  if (window.__diabecHumanCallMounted) return;
  window.__diabecHumanCallMounted = true;

  // Human-call talks to the Fly backend directly: the /web-calls/* and
  // /voice/guest/* endpoints live ONLY on diabec-api.fly.dev. We must NOT reuse
  // the shared window.DIABEC_API_BASE — on a Shopify storefront that global is
  // set to the Cloudflare Pages site (for analytics → CF Functions), which has
  // no /web-calls routes (WS 404 + consent CORS). Dedicated override only.
  var API = (window.DIABEC_HUMAN_CALL_API || 'https://diabec-api.fly.dev').replace(/\/$/, '');
  var WS_BASE = API.replace(/^http/, 'ws');
  var LK_UMD = 'https://cdn.jsdelivr.net/npm/livekit-client@2/dist/livekit-client.umd.min.js';

  var state = {
    ws: null, wsRetry: 0, wsWanted: false,
    room: null, roomName: null,
    timer: null, startedAt: 0,
    muted: false, connecting: false, inCall: false,
    pendingIncoming: false,
    // Identity + consent
    visitorKey: null, consent: null, returning: false, visits: 1,
    // Web Push (Step 2) + ringtone (Step 1)
    pushEnabled: false,
    ringCtx: null, ringNodes: null, ringTimer: null,
  };

  // Exact consent wording shown to the user — also sent to the audit endpoint.
  // Compliance: human (not AI/bot), purpose-limited, decline allowed,
  // withdrawable, supplement disclaimer, no health claims.
  var CONSENT_WORDING =
    'Diabec can connect you with a real specialist (a person, not a bot) by ' +
    'voice on this page. May we reach out to you here if we can help? You can ' +
    'decline and still start a call yourself anytime, and change your choice later.';

  // ─────────── Identity + consent (localStorage) ───────────
  function lsGet(k) { try { return localStorage.getItem(k); } catch (_) { return null; } }
  function lsSet(k, v) { try { localStorage.setItem(k, v); } catch (_) {} }
  function nowISO() { try { return new Date().toISOString(); } catch (_) { return ''; } }
  function newKey() {
    try { if (window.crypto && crypto.randomUUID) return 'v-' + crypto.randomUUID().replace(/-/g, '').slice(0, 12); } catch (_) {}
    return 'v-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }
  // Create-or-load the persistent identity. Only called once consent is GRANTED
  // (we never fingerprint a visitor who hasn't opted in). Bumps the visit count
  // for returning devices.
  function initIdentity() {
    var key = lsGet('dhc_visitor_key');
    if (key) {
      var visits = (parseInt(lsGet('dhc_visits') || '1', 10) || 1) + 1;
      lsSet('dhc_visits', String(visits));
      lsSet('dhc_last_seen', nowISO());
      state.visitorKey = key; state.returning = true; state.visits = visits;
    } else {
      key = newKey();
      lsSet('dhc_visitor_key', key);
      lsSet('dhc_first_seen', nowISO());
      lsSet('dhc_visits', '1');
      lsSet('dhc_last_seen', nowISO());
      state.visitorKey = key; state.returning = false; state.visits = 1;
    }
  }
  function recordConsent(decision) {
    try {
      fetch(API + '/web-calls/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
        body: JSON.stringify({
          visitor_key: state.visitorKey || newKey(),
          decision: decision,
          wording: CONSENT_WORDING,
          page: location.pathname,
          lang: navigator.language || '',
          client_time: nowISO(),
        }),
      }).catch(function () {});
    } catch (_) {}
  }

  // ─── icons ───
  var PHONE = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56a.977.977 0 00-1.01.24l-1.57 1.97a15.547 15.547 0 01-6.99-6.99l1.95-1.57c.27-.27.35-.65.24-1.01-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 2.94 3 3.18 3 3.93 3 13.28 10.72 21 20.07 21c.71 0 .93-.63.93-1.18v-3.44c0-.55-.45-1-.99-1z"/></svg>';
  var MIC = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3zm5-3a5 5 0 01-10 0H5a7 7 0 006 6.92V21h2v-3.08A7 7 0 0019 11h-2z"/></svg>';
  var MIC_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zM14.98 11.17c0-.06.02-.11.02-.17V5a3 3 0 00-5.94-.6L14.98 11.17zM4.27 3L3 4.27l6.01 6.01V11a3 3 0 003 3c.22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52a5 5 0 01-5-5H5a7 7 0 0011.21 5.6L19.73 21 21 19.73 4.27 3z"/></svg>';

  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) { if (k === 'class') n.className = attrs[k]; else if (k === 'text') n.textContent = attrs[k]; else n.setAttribute(k, attrs[k]); }
    if (html !== undefined) n.innerHTML = html;
    return n;
  }

  // ─── DOM refs ───
  var fabEl, modalEl, panelEl, panelTitle, panelTimer, panelMic, incomingEl, consentEl;

  function buildFab() {
    var btn = el('button', { class: 'dhc-fab', type: 'button', 'aria-label': 'Talk to a Diabec specialist (human) — opens a dialog before connecting', 'aria-haspopup': 'dialog', title: 'Talk to a specialist' });
    btn.innerHTML = PHONE + '<span class="dhc-fab-badge">LIVE</span><span class="dhc-fab-tooltip" aria-hidden="true">Talk to a specialist</span>';
    btn.addEventListener('click', openModal);
    return btn;
  }

  function buildModal() {
    var overlay = el('div', { class: 'dhc-modal-overlay', role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': 'dhc-title' });
    var m = el('div', { class: 'dhc-modal' });
    m.appendChild(el('h2', { id: 'dhc-title', text: 'Talk to a Diabec specialist' }));
    m.appendChild(el('p', { text: 'Start a free voice call with a member of our team. You’ll need to allow microphone access. If everyone is busy you can leave a message instead.' }));
    var d = el('div', { class: 'dhc-disclaimer' });
    d.innerHTML = 'You’ll be connected to a <strong>human specialist</strong>, not an AI. Diabec is a food supplement, not a medicine, and our team cannot diagnose or prescribe. In a medical emergency call your local emergency number.';
    m.appendChild(d);
    var actions = el('div', { class: 'dhc-actions' });
    var cancel = el('button', { class: 'dhc-btn dhc-btn-secondary', type: 'button', text: 'Cancel' });
    var start = el('button', { class: 'dhc-btn dhc-btn-primary', type: 'button', text: 'Start call' });
    cancel.addEventListener('click', closeModal);
    start.addEventListener('click', startVisitorCall);
    actions.appendChild(cancel); actions.appendChild(start);
    m.appendChild(actions);
    overlay.appendChild(m);
    overlay.addEventListener('click', function (e) { if (e.target === overlay) closeModal(); });
    document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && overlay.classList.contains('dhc-open')) closeModal(); });
    return overlay;
  }
  function openModal() { modalEl.classList.add('dhc-open'); var b = modalEl.querySelector('.dhc-btn-primary'); if (b) b.focus(); }
  function closeModal() { modalEl.classList.remove('dhc-open'); }

  function buildPanel() {
    var p = el('div', { class: 'dhc-panel', role: 'status', 'aria-live': 'polite' });
    var status = el('div', { class: 'dhc-status' });
    panelTitle = el('div', { class: 'dhc-title', text: 'Connecting…' });
    panelTimer = el('div', { class: 'dhc-timer', text: '' });
    status.appendChild(panelTitle); status.appendChild(panelTimer);
    panelMic = el('button', { class: 'dhc-mic', type: 'button', 'aria-label': 'Mute microphone' });
    panelMic.innerHTML = MIC; panelMic.addEventListener('click', toggleMute);
    var end = el('button', { class: 'dhc-end', type: 'button', text: 'End' });
    end.addEventListener('click', endCall);
    p.appendChild(status); p.appendChild(panelMic); p.appendChild(end);
    return p;
  }

  function buildIncoming() {
    var b = el('div', { class: 'dhc-incoming', role: 'alertdialog', 'aria-label': 'Incoming call from Diabec' });
    var txt = el('div', { class: 'dhc-inc-text' });
    txt.innerHTML = '<strong>Diabec Support</strong><span>is calling you…</span>';
    var accept = el('button', { class: 'dhc-inc-accept', type: 'button', text: 'Accept' });
    var decline = el('button', { class: 'dhc-inc-decline', type: 'button', text: 'Decline' });
    accept.addEventListener('click', acceptIncoming);
    decline.addEventListener('click', declineIncoming);
    b.appendChild(txt); b.appendChild(decline); b.appendChild(accept);
    return b;
  }

  function toast(msg, ms) {
    var t = el('div', { class: 'dhc-toast', role: 'alert' }); t.textContent = msg;
    document.body.appendChild(t); setTimeout(function () { t.remove(); }, ms || 5000);
  }
  function showPanel() { panelEl.classList.add('dhc-open'); }
  function hidePanel() { panelEl.classList.remove('dhc-open'); }
  function setTitle(t) { panelTitle.textContent = t; }

  // ─── LiveKit loader ───
  function ensureLiveKit() {
    return new Promise(function (resolve, reject) {
      if (window.LivekitClient) return resolve(window.LivekitClient);
      var s = document.createElement('script'); s.src = LK_UMD;
      s.onload = function () { window.LivekitClient ? resolve(window.LivekitClient) : reject(new Error('LiveKit failed to load')); };
      s.onerror = function () { reject(new Error('LiveKit failed to load')); };
      document.head.appendChild(s);
    });
  }

  // ─── Signaling WebSocket (presence) ───
  // Only connects when wanted: either consent is granted (proactive presence)
  // or the visitor self-initiates a call (implied consent for that call).
  function connectWS() {
    state.wsWanted = true;
    if (state.ws && (state.ws.readyState === 0 || state.ws.readyState === 1)) return;
    try { state.ws = new WebSocket(WS_BASE + '/web-calls/signal?role=visitor'); }
    catch (e) { return; }
    state.ws.onopen = function () {
      state.wsRetry = 0;
      var v = window.DIABEC_VISITOR || {};
      sendWS({
        type: 'hello',
        name: v.name || 'Website visitor',
        user_id: v.user_id || null,
        page: location.pathname + (document.title ? ' — ' + document.title.slice(0, 60) : ''),
        visitor_key: state.visitorKey,
        consent: state.consent || 'unknown',
        returning: state.returning,
        visits: state.visits,
        push: state.pushEnabled === true,
        push_reason: pushReason(),
        origin: location.origin,
      });
    };
    state.ws.onmessage = function (ev) { var m; try { m = JSON.parse(ev.data); } catch (_) { return; } onWS(m); };
    state.ws.onclose = function () {
      if (!state.wsWanted) return;
      var delay = Math.min(1000 * Math.pow(2, state.wsRetry++), 20000);
      setTimeout(connectWS, delay);
    };
    state.ws.onerror = function () {};
  }
  // Ensure the WS is OPEN before sending (self-initiated calls when presence
  // wasn't already connected). Resolves once open or after ~5s.
  function ensureConnected() {
    return new Promise(function (resolve) {
      if (state.ws && state.ws.readyState === 1) return resolve(true);
      connectWS();
      var tries = 0;
      var iv = setInterval(function () {
        if (state.ws && state.ws.readyState === 1) { clearInterval(iv); resolve(true); }
        else if (++tries > 50) { clearInterval(iv); resolve(false); }
      }, 100);
    });
  }
  function sendWS(o) { if (state.ws && state.ws.readyState === 1) state.ws.send(JSON.stringify(o)); }

  function onWS(m) {
    switch (m.type) {
      case 'welcome': state.visitorId = m.visitor_id; break;
      case 'agent-accepted':
        // Our outgoing ring was picked up; we're already in the room.
        state.connecting = false; onConnected(); break;
      case 'agent-unavailable':
        failCall('All specialists are busy right now. Please try again shortly or use the AI assistant.'); break;
      case 'incoming-call':
        showIncoming(); break;
      case 'call-ended':
        if (state.inCall || state.connecting) { toast('Call ended.'); cleanup(); }
        hideIncoming(); break;
    }
  }

  // Map a thrown error (mic problems are by far the common case) to a clear,
  // actionable message instead of a generic "try again".
  function callErrMsg(err) {
    var n = err && err.name;
    if (n === 'NotAllowedError' || n === 'SecurityError') return 'Microphone permission denied. Allow mic access in your browser to talk.';
    if (n === 'NotFoundError' || n === 'OverconstrainedError') return 'No microphone found on this device. Connect a mic, or call from your phone.';
    if (n === 'NotReadableError' || n === 'AbortError') return 'Your microphone is in use by another app. Close it (Zoom, WhatsApp, Teams) and try again.';
    return 'We couldn’t start the call. Please try again.';
  }

  // ─── Visitor-initiated call ───
  async function startVisitorCall() {
    if (state.connecting || state.inCall) return;
    closeModal(); showPanel(); setTitle('Connecting…');
    panelTimer.innerHTML = '<span class="dhc-spinner"></span>Reaching a specialist…';
    state.connecting = true;
    try {
      await ensureConnected();          // self-initiated → connect presence if it wasn't
      var LK = await ensureLiveKit();
      var tokenData = await mintRoom();
      await joinRoom(LK, tokenData);
      sendWS({ type: 'ring', room: state.roomName });
      // Watchdog: if no agent picks up in 35s, bail with a friendly message.
      setTimeout(function () { if (state.connecting) failCall('We couldn’t reach a specialist. Please try again or use the AI assistant.'); }, 35000);
    } catch (err) {
      console.error('[dhc] startVisitorCall', err && err.name, err);
      failCall(callErrMsg(err));
    }
  }

  async function mintRoom() {
    var res = await fetch(API + '/voice/guest/token', { method: 'POST' });
    if (res.status === 429) throw new Error('rate_limited');
    if (!res.ok) throw new Error('voice-token ' + res.status);
    var data = await res.json();
    state.roomName = data.room;
    return data;
  }

  async function joinRoom(LK, tokenData) {
    var room = new LK.Room({ adaptiveStream: true, dynacast: true });
    state.room = room;
    room.on(LK.RoomEvent.ParticipantConnected, onConnected);
    room.on(LK.RoomEvent.Disconnected, function () { cleanup(); });
    room.on(LK.RoomEvent.TrackSubscribed, function (track) {
      if (track.kind === 'audio') { var a = track.attach(); a.autoplay = true; a.style.display = 'none'; document.body.appendChild(a); }
    });
    await room.connect(tokenData.url, tokenData.token);
    await room.localParticipant.setMicrophoneEnabled(true);
  }

  function onConnected() {
    if (state.inCall) return;
    state.connecting = false; state.inCall = true;
    sendWS({ type: 'in-call' });
    setTitle('Connected'); hideIncoming();
    if (state.timer) clearInterval(state.timer);
    state.startedAt = Date.now(); state.timer = setInterval(updateTimer, 1000); updateTimer();
  }

  function updateTimer() {
    if (!state.startedAt) return;
    var s = Math.floor((Date.now() - state.startedAt) / 1000);
    panelTimer.textContent = String(Math.floor(s / 60)).padStart(2, '0') + ':' + String(s % 60).padStart(2, '0');
  }

  // ─── Agent-initiated incoming ───
  function showIncoming() { state.pendingIncoming = true; incomingEl.classList.add('dhc-open'); startRingtone(); }
  function hideIncoming() { state.pendingIncoming = false; incomingEl.classList.remove('dhc-open'); stopRingtone(); }

  async function acceptIncoming() {
    hideIncoming();
    showPanel(); setTitle('Connecting…'); panelTimer.innerHTML = '<span class="dhc-spinner"></span>Connecting…';
    state.connecting = true;
    try {
      var LK = await ensureLiveKit();
      var tokenData = await mintRoom();
      await joinRoom(LK, tokenData);
      sendWS({ type: 'accept', room: state.roomName });
    } catch (err) {
      console.error('[dhc] acceptIncoming', err && err.name, err);
      sendWS({ type: 'decline' });
      failCall(callErrMsg(err));
    }
  }
  function declineIncoming() { hideIncoming(); sendWS({ type: 'decline' }); }

  // ─── Controls ───
  function toggleMute() {
    if (!state.room) return;
    state.muted = !state.muted;
    state.room.localParticipant.setMicrophoneEnabled(!state.muted);
    panelMic.classList.toggle('dhc-muted', state.muted);
    panelMic.innerHTML = state.muted ? MIC_OFF : MIC;
    panelMic.setAttribute('aria-label', state.muted ? 'Unmute microphone' : 'Mute microphone');
  }

  async function endCall() {
    sendWS({ type: 'end' });
    if (state.room) { try { await state.room.disconnect(); } catch (_) {} }
    cleanup();
  }

  function failCall(msg) {
    if (state.roomName) sendWS({ type: 'cancel' });
    toast(msg, 7000); cleanup();
  }

  function cleanup() {
    if (state.timer) { clearInterval(state.timer); state.timer = null; }
    if (state.room) { try { state.room.disconnect(); } catch (_) {} }
    document.querySelectorAll('body > audio').forEach(function (a) { a.remove(); });
    state.room = null; state.roomName = null; state.connecting = false; state.inCall = false; state.muted = false; state.startedAt = 0;
    hidePanel(); hideIncoming();
    panelTimer.textContent = ''; panelMic.classList.remove('dhc-muted'); panelMic.innerHTML = MIC;
  }

  // Continuous ringtone (Step 1): a two-tone burst repeating until the visitor
  // Answers/Declines. Uses one persistent AudioContext (resumed on the consent
  // click gesture so autoplay policies don't block it).
  function startRingtone() {
    stopRingtone();
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!state.ringCtx) state.ringCtx = new Ctx();
      if (state.ringCtx.state === 'suspended') state.ringCtx.resume();
    } catch (_) { return; }
    var burst = function () {
      var ctx = state.ringCtx; if (!ctx) return;
      var base = ctx.currentTime;
      [[0, 880], [0.4, 660]].forEach(function (pair) {
        var o = ctx.createOscillator(), g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine'; o.frequency.value = pair[1];
        var s = base + pair[0];
        g.gain.setValueAtTime(0.0001, s);
        g.gain.exponentialRampToValueAtTime(0.28, s + 0.03);
        g.gain.exponentialRampToValueAtTime(0.0001, s + 0.32);
        o.start(s); o.stop(s + 0.34);
      });
    };
    burst();
    state.ringTimer = setInterval(burst, 2000);
  }
  function stopRingtone() {
    if (state.ringTimer) { clearInterval(state.ringTimer); state.ringTimer = null; }
  }
  // Resume the AudioContext during a user gesture so the ringtone can play
  // later without a gesture (browser autoplay requirement).
  function primeAudio() {
    try {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!state.ringCtx) state.ringCtx = new Ctx();
      if (state.ringCtx.state === 'suspended') state.ringCtx.resume();
    } catch (_) {}
  }

  // ─── Web Push permissions flow (Step 2) ───
  // After consent: request Notification permission, register the SW, subscribe
  // with the server's VAPID key, and store the subscription keyed by visitor_key.
  // Entirely best-effort — if the user blocks notifications, presence + self-
  // initiated calls still work; only the offline (tab-closed) reach is lost.
  // Why is push unavailable? Reported to the agent dashboard so it can show
  // *why* an offline visitor can't be reached (e.g. iPhone needs Add-to-Home).
  function pushReason() {
    if (state.pushEnabled === true) return null;
    try {
      var hasPush = ('Notification' in window) && ('PushManager' in window) && ('serviceWorker' in navigator);
      var isIOS = /iP(hone|ad|od)/.test(navigator.userAgent) ||
                  (/Mac/.test(navigator.platform) && navigator.maxTouchPoints > 1);
      var standalone = (window.navigator.standalone === true) ||
                       (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
      if (!hasPush) return (isIOS && !standalone) ? 'ios-install' : 'unsupported';
      if (window.Notification && Notification.permission === 'denied') return 'denied';
      return 'not-subscribed';
    } catch (_) { return 'unknown'; }
  }

  async function enablePush() {
    try {
      if (!('serviceWorker' in navigator) || !('PushManager' in window) || !('Notification' in window)) return;
      var perm = await Notification.requestPermission();
      if (perm !== 'granted') return;
      // SW URL is configurable: on a Shopify storefront we CANNOT serve a SW at
      // the site root (/sw.js → 302/404), so the theme passes the asset URL via
      // window.DIABEC_SW_URL. A narrow asset-path scope is fine for push: the
      // push event + notificationclick (openWindow/postMessage) don't need root
      // scope. Default '/sw.js' keeps the Cloudflare marketing site working.
      var swUrl = window.DIABEC_SW_URL || '/sw.js';
      var reg = await navigator.serviceWorker.register(swUrl);
      await navigator.serviceWorker.ready;
      var cfg = await fetch(API + '/web-calls/config').then(function (r) { return r.json(); }).catch(function () { return null; });
      if (!cfg || !cfg.vapid_public_key) return;
      var sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(cfg.vapid_public_key),
        });
      }
      var v = window.DIABEC_VISITOR || {};
      await fetch(API + '/web-calls/push-subscribe', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, keepalive: true,
        body: JSON.stringify({ visitor_key: state.visitorKey, subscription: sub.toJSON(), user_id: v.user_id || null }),
      });
      state.pushEnabled = true;
      // Re-announce so the agent roster marks this visitor push-reachable even
      // after the tab closes (the first hello fired before push was ready).
      sendWS({
        type: 'hello', name: v.name || 'Website visitor', user_id: v.user_id || null,
        page: location.pathname + (document.title ? ' — ' + document.title.slice(0, 60) : ''),
        visitor_key: state.visitorKey, consent: state.consent || 'unknown',
        returning: state.returning, visits: state.visits, push: true, push_reason: null, origin: location.origin,
      });
    } catch (e) { /* best-effort */ }
  }
  function urlB64ToUint8Array(base64String) {
    var padding = '='.repeat((4 - base64String.length % 4) % 4);
    var base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    var raw = atob(base64);
    var arr = new Uint8Array(raw.length);
    for (var i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return arr;
  }
  // If a returning visitor already granted consent in a previous session,
  // (re)ensure their push subscription on load.
  function maybeEnablePushOnLoad() {
    if (state.consent === 'granted' && 'Notification' in window && Notification.permission === 'granted') {
      enablePush();
    }
  }

  // ─── Mount ───
  // ─────────── First-visit communication consent ───────────
  function buildConsent() {
    var b = el('div', { class: 'dhc-consent', role: 'dialog', 'aria-label': 'Live contact consent', 'aria-live': 'polite' });
    var txt = el('div', { class: 'dhc-consent-text' });
    txt.innerHTML =
      '<strong>Live support while you browse</strong>' +
      '<span>' + CONSENT_WORDING + '</span>' +
      '<small>Used only to talk with you about Diabec. Food supplement, not a substitute for a varied diet. You can withdraw consent anytime.</small>';
    var actions = el('div', { class: 'dhc-consent-actions' });
    var no = el('button', { class: 'dhc-consent-no', type: 'button', text: 'No thanks' });
    var yes = el('button', { class: 'dhc-consent-yes', type: 'button', text: 'Allow live contact' });
    no.addEventListener('click', function () { decideConsent('denied'); });
    yes.addEventListener('click', function () { decideConsent('granted'); });
    actions.appendChild(no); actions.appendChild(yes);
    b.appendChild(txt); b.appendChild(actions);
    return b;
  }
  function showConsent() { if (consentEl) consentEl.classList.add('dhc-open'); }
  function hideConsent() { if (consentEl) consentEl.classList.remove('dhc-open'); }
  function decideConsent(decision) {
    state.consent = decision;
    lsSet('dhc_comm_consent', decision);
    lsSet('dhc_consent_at', nowISO());
    hideConsent();
    if (decision === 'granted') {
      initIdentity();          // create/load persistent ID (opt-in only)
      recordConsent('granted');
      primeAudio();            // this click is the gesture that unlocks ringtone audio
      enablePush();            // Step 2 plumbing: register SW + subscribe (best-effort)
      connectWS();             // proactive presence — agent can now see + ring
    }
    // 'denied': no key, no presence, no server record. The FAB still works;
    // a self-initiated call connects ephemerally (implied consent for it).
  }

  // ─────────── Mount ───────────
  function mount() {
    fabEl = buildFab(); modalEl = buildModal(); panelEl = buildPanel();
    incomingEl = buildIncoming(); consentEl = buildConsent();
    document.body.appendChild(fabEl); document.body.appendChild(modalEl);
    document.body.appendChild(panelEl); document.body.appendChild(incomingEl);
    document.body.appendChild(consentEl);

    state.consent = lsGet('dhc_comm_consent');   // 'granted' | 'denied' | null
    // Did we arrive here from a push notification? (sw.js opens with this flag)
    var answering = /[?&]dhc_answer=1/.test(location.search);

    if (state.consent === 'granted') {
      initIdentity();
      primeAudio();                              // best-effort (may need a later gesture)
      connectWS();                               // returning consented visitor → presence
      maybeEnablePushOnLoad();                   // re-ensure push subscription
    } else if (state.consent === 'denied') {
      // Respect the choice: no proactive presence. FAB remains available.
      if (answering) { initIdentity(); connectWS(); }  // they explicitly tapped a call notif
    } else {
      // First visit (or choice cleared) → ask. Delay slightly so it doesn't
      // collide with the cookie banner on load.
      setTimeout(showConsent, 1200);
    }

    // sw.js asks an already-open tab to wake + answer when the notification is clicked.
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', function (e) {
        if (e.data && e.data.type === 'dhc-answer') { primeAudio(); connectWS(); }
      });
    }
    window.addEventListener('beforeunload', function () { if (state.inCall) sendWS({ type: 'end' }); });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', mount); else mount();
})();
